import { useState, useMemo, useEffect, useRef } from 'react';
import { useAvailableYears, useSubjects, useSubjectsForYearFromClasses, useSubjectsForYearsFromClasses, useUserStateSubjects, useTaskTypes, useResponseTypes } from './useSubjects';
import { useMarkingToolStates } from './useUserStates';

/**
 * Shared hook for settings cascading filters across all marking tools.
 * Handles: Year → Subjects → Task Types → Response Types
 *
 * Data flows from /api/v1/subjects API with proper filtering:
 * - Years: All available years
 * - Subjects: Filtered by selected year (via useSubjectsForYearFromClasses)
 * - Task Types: Filtered by selected subjects
 * - Response Types: Filtered by selected subjects AND task types
 *
 * @param {Object} options
 * @param {string} options.state - User's state (e.g., 'NSW', 'VIC')
 * @param {Array} options.initialYears - Pre-selected years
 * @param {Array} options.initialSubjects - Pre-selected subjects
 * @param {Array} options.initialTaskTypes - Pre-selected task types
 * @param {Array} options.initialResponseTypes - Pre-selected response types
 * @param {boolean} options.enableYearFilter - Whether to filter subjects by year (default: true for create pages)
 * @param {boolean} options.managedMode - If true, hook manages its own state (for detail pages with item prop)
 * @param {Object} options.item - Existing item data (for managed mode)
 * @param {boolean} options.isOpen - When true (modal open), re-sync from item so current selection is visible
 *
 * @returns {Object} Filter options and selected values with setters
 */
const useSettingsFilters = ({
  state,
  initialYears = [],
  initialSubjects = [],
  initialTaskTypes = [],
  initialResponseTypes = [],
  enableYearFilter = true,
  managedMode = false,
  item = null,
  isOpen = false,
}) => {
  // Helper to extract subject code from various formats
  const getSubjectCode = (s) => {
    if (typeof s === 'object' && s !== null) {
      return s.code || s.subjectCode || s.value || s;
    }
    return s;
  };

  // Helper to extract subject name from various formats
  const getSubjectName = (s) => {
    if (typeof s === 'object' && s !== null) {
      return s.title || s.subjectName || s.name || s.label || s.code || s.subjectCode || s.value || s;
    }
    return s;
  };

  // Internal state (for managed mode)
  const [internalYears, setInternalYears] = useState(initialYears);
  const [internalSubjects, setInternalSubjects] = useState(initialSubjects);
  const [internalTaskTypes, setInternalTaskTypes] = useState(initialTaskTypes);
  const [internalResponseTypes, setInternalResponseTypes] = useState(initialResponseTypes);

  // Store original subject objects from item to preserve names for fallback display
  const [originalSubjectObjects, setOriginalSubjectObjects] = useState([]);

  // Track whether we've already synced for this modal open so we don't keep restoring item values
  const hasSyncedRef = useRef(false);

  // Reset sync flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasSyncedRef.current = false;
    }
  }, [isOpen]);

  // Sync internal state from item when modal first opens (managed mode)
  // So the user always sees current item's year/subject (e.g. English) in the popup.
  useEffect(() => {
    if (managedMode && item && isOpen && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      setInternalYears(item.metadata?.years?.length ? item.metadata.years : (item.years?.length ? item.years : (item.year ? [item.year] : [])));
      const rawSubjects = item.metadata?.subjects?.length ? item.metadata.subjects : (item.subjects || []);
      setOriginalSubjectObjects(rawSubjects.filter(Boolean));
      setInternalSubjects(
        rawSubjects
          .map(getSubjectCode)
          .filter(Boolean)
      );
      setInternalTaskTypes(item.metadata?.taskTypes?.length ? item.metadata.taskTypes : (item.taskTypes || (item.taskType ? [item.taskType] : [])));
      setInternalResponseTypes(item.metadata?.responseTypes?.length ? item.metadata.responseTypes : (item.responseTypes || (item.responseType ? [item.responseType] : [])));
    }
  }, [managedMode, item, isOpen]);

  // Always use internal state for selected values
  const selectedYears = internalYears;
  const setSelectedYears = setInternalYears;

  const selectedSubjects = internalSubjects;
  const setSelectedSubjects = setInternalSubjects;

  const selectedTaskTypes = internalTaskTypes;
  const setSelectedTaskTypes = setInternalTaskTypes;

  const selectedResponseTypes = internalResponseTypes;
  const setSelectedResponseTypes = setInternalResponseTypes;

  const { years: availableYears } = useAvailableYears();
  const yearForFilter = selectedYears?.find((y) => availableYears?.includes(y)) ?? selectedYears?.[0];
  const yearsForFilter = Array.isArray(selectedYears) && selectedYears.length
    ? selectedYears
    : (yearForFilter != null ? [yearForFilter] : []);

  // Individual-teacher state scope (empty for school/stateless users → no-op).
  const scopeStates = useMarkingToolStates();
  const { subjects: subjectsByYear } = useSubjects(state, yearForFilter);
  const { subjects: subjectsFromClasses } = useSubjectsForYearFromClasses(yearForFilter, scopeStates);
  const { subjects: subjectsForSelectedYears } = useSubjectsForYearsFromClasses(selectedYears, scopeStates);
  const { subjects: allClassSubjects } = useUserStateSubjects(scopeStates);

  const rawSubjects = (enableYearFilter && yearForFilter)
    ? (subjectsByYear?.length ? subjectsByYear : subjectsFromClasses)
    : (subjectsForSelectedYears?.length ? subjectsForSelectedYears : allClassSubjects) || [];

  const subjectsSourceForEnrich = subjectsForSelectedYears?.length ? subjectsForSelectedYears : subjectsFromClasses;
  const availableSubjects = useMemo(() => {
    const enriched = rawSubjects.map((s) => {
      const fromClass = subjectsSourceForEnrich?.find((c) => c.code === s.code || c.subjectCode === s.subjectCode);
      return fromClass
        ? { ...s, taskTypes: fromClass.taskTypes || [], responseTypes: fromClass.responseTypes || [] }
        : s;
    });

    const selectedCodes = new Set(enriched.map(s => s.code || s.subjectCode || s.value));
    const selectedSubjectsToAdd = (selectedSubjects || [])
      .map(code => {
        if (selectedCodes.has(code)) return null;
        const fromClass = subjectsSourceForEnrich?.find((c) =>
          (c.code || c.subjectCode || c.value) === code ||
          (c.title || c.subjectName || c.name) === code
        );
        if (fromClass) return fromClass;
        // Check original subject objects from item to get the display name
        const originalObj = originalSubjectObjects?.find((s) => {
          if (typeof s !== 'object' || s === null) return s === code;
          const objCode = s.code || s.subjectCode || s.value;
          const objName = s.title || s.subjectName || s.name;
          return objCode === code || objName === code;
        });
        if (originalObj && typeof originalObj === 'object') {
          return {
            code: originalObj.subjectCode || originalObj.code || code,
            title: originalObj.subjectName || originalObj.title || originalObj.name || code,
            subjectCode: originalObj.subjectCode || originalObj.code || code,
            subjectName: originalObj.subjectName || originalObj.title || originalObj.name || code,
          };
        }
        // Legacy string-form original (or any still-unresolved selection):
        // preserve it so state/year scoping can't silently strip an out-of-state
        // subject on save — ItemSettingsModal needs it present to resolve it.
        if (code) {
          return { code, title: code, subjectCode: code, subjectName: code };
        }
        return null;
      })
      .filter(Boolean);

    return [...enriched, ...selectedSubjectsToAdd];
  }, [rawSubjects, subjectsSourceForEnrich, selectedSubjects, originalSubjectObjects]);

  // Get task types - filtered by selected subjects (only when subjects are selected)
  const { taskTypes: apiTaskTypes } = useTaskTypes(selectedSubjects, state, yearsForFilter.length ? yearsForFilter : yearForFilter);

  // No subjects selected = no task types available
  const availableTaskTypes = useMemo(
    () => (selectedSubjects.length ? apiTaskTypes : []),
    [apiTaskTypes, selectedSubjects]
  );

  // Get response types - filtered by selected subjects AND task types
  const { responseTypes: apiResponseTypes } = useResponseTypes(
    selectedSubjects,
    selectedTaskTypes,
    state,
    yearsForFilter.length ? yearsForFilter : yearForFilter
  );

  // No subjects or no task types = no response types available
  const availableResponseTypes = useMemo(
    () => (selectedSubjects.length && selectedTaskTypes.length ? apiResponseTypes : []),
    [apiResponseTypes, selectedSubjects, selectedTaskTypes]
  );

  // Cascading validation: clear invalid selections whenever available options change
  useEffect(() => {
    if (!selectedTaskTypes.length) return;
    if (!availableTaskTypes.length) {
      console.log('[DEBUG] clearing all task types (no available)');
      setSelectedTaskTypes([]);
      return;
    }
    const validValues = new Set(availableTaskTypes.map(t => t.value ?? t));
    const filtered = selectedTaskTypes.filter(t => validValues.has(t));
    if (filtered.length !== selectedTaskTypes.length) {
      setSelectedTaskTypes(filtered);
    }
  }, [availableTaskTypes, selectedTaskTypes]);

  useEffect(() => {
    if (!selectedResponseTypes.length) return;
    if (!availableResponseTypes.length) {
      setSelectedResponseTypes([]);
      return;
    }
    const validValues = new Set(availableResponseTypes.map(r => r.value ?? r));
    const filtered = selectedResponseTypes.filter(r => validValues.has(r));
    if (filtered.length !== selectedResponseTypes.length) {
      setSelectedResponseTypes(filtered);
    }
  }, [availableResponseTypes, selectedResponseTypes]);

  const createYearSetter = (externalSetter) => (newYears) => {
    const setter = externalSetter || setSelectedYears;
    if (setter) setter(newYears);
    const subjectSetter = externalSetter?.setSelectedSubjects || setSelectedSubjects;
    const taskTypeSetter = externalSetter?.setSelectedTaskTypes || setSelectedTaskTypes;
    const responseTypeSetter = externalSetter?.setSelectedResponseTypes || setSelectedResponseTypes;
    if (subjectSetter) subjectSetter([]);
    if (taskTypeSetter) taskTypeSetter([]);
    if (responseTypeSetter) responseTypeSetter([]);
  };

  const createSubjectSetter = (externalSetter) => (newSubjects) => {
    // When subjects change, clear task types and response types that may no longer be valid
    const setter = externalSetter || setSelectedSubjects;
    if (setter) {
      setter(newSubjects);
    }
    const taskTypeSetter = externalSetter?.setSelectedTaskTypes || setSelectedTaskTypes;
    const responseTypeSetter = externalSetter?.setSelectedResponseTypes || setSelectedResponseTypes;
    if (taskTypeSetter) taskTypeSetter([]);
    if (responseTypeSetter) responseTypeSetter([]);
  };

  const createTaskTypeSetter = (externalSetter) => (newTaskTypes) => {
    // When task types change, clear response types that may no longer be valid
    const setter = externalSetter || setSelectedTaskTypes;
    if (setter) {
      setter(newTaskTypes);
    }
    const responseTypeSetter = externalSetter?.setSelectedResponseTypes || setSelectedResponseTypes;
    if (responseTypeSetter) responseTypeSetter([]);
  };

  const createResponseTypeSetter = (externalSetter) => (newResponseTypes) => {
    const setter = externalSetter || setSelectedResponseTypes;
    if (setter) {
      setter(newResponseTypes);
    }
  };

  // Create label mappings for SettingsTagsDisplay
  const taskTypeLabels = useMemo(() => {
    return Object.fromEntries(
      availableTaskTypes.map((t) => [
        t.value ?? t,
        t.label ?? t.title ?? t.value ?? t
      ])
    );
  }, [availableTaskTypes]);

  const responseTypeLabels = useMemo(() => {
    return Object.fromEntries(
      availableResponseTypes.map((r) => [
        r.value ?? r,
        r.label ?? r.title ?? r.value ?? r
      ])
    );
  }, [availableResponseTypes]);

  return {
    // Available options (filtered based on selections)
    availableYears,
    availableSubjects,
    availableTaskTypes,
    availableResponseTypes,

    // Selected values (with setters that cascade)
    selectedYears,
    selectedSubjects,
    selectedTaskTypes,
    selectedResponseTypes,

    // Label mappings for SettingsTagsDisplay
    taskTypeLabels,
    responseTypeLabels,

    // Setter creators (pass external state setters from parent component)
    createYearSetter,
    createSubjectSetter,
    createTaskTypeSetter,
    createResponseTypeSetter,

    // Internal state (for managed mode or when parent wants direct access)
    internalYears,
    setInternalYears,
    internalSubjects,
    setInternalSubjects,
    internalTaskTypes,
    setInternalTaskTypes,
    internalResponseTypes,
    setInternalResponseTypes,
  };
};

export default useSettingsFilters;
